import React, { useContext, createContext, ReactNode, useState } from 'react';
import { submitTracking } from '../Tracking/utils/shopifyActionTrackingHelper';
import { trackWishlistAlgoliaHit } from '@jbhi-fi/utils/src/ga4Tracking';
import { CustomerPreferences } from '../types';

export type WishlistActionType = 'addItem' | 'removeItem';

export type WishlistNotLoggedInAction = 'none' | 'login' | 'createAccount';

export interface CustomerContextInterface {
  isLoggedIn?: boolean;
  isPerks: boolean;
  birthdayMonth?: number;
  perksJoinDate?: Date;
  customerName?: string | null;
  customerPreferences?: CustomerPreferences;
  addressesCount?: number;
  itemsInWishlist?: Array<string>;
  isWishlistLoading?: boolean;
  setIsWishlistLoading: (IsWishlistLoading: boolean) => void;
  setWishlistItems: React.Dispatch<React.SetStateAction<Array<string> | undefined>>;
  updateWishlist?: (args: {
    actionType: WishlistActionType;
    sku: string;
    variantId: number;
    notLoggedInAction?: WishlistNotLoggedInAction;
    eventData?: any;
    event?: any;
  }) => Promise<{ success: boolean; error?: unknown }>;
  createAccount?: (event: React.MouseEvent, eventData?: any, returnUrl?: string) => void;
  logIn?: (event: React.MouseEvent, eventData?: any, returnUrl?: string) => void;
  handleUpdateCustomerPreferences: (key: string, value: string) => Promise<boolean>;
}

export const CustomerContext = createContext<CustomerContextInterface | null>(null);

export type CustomerContextProviderType = {
  children: ReactNode;
  value: Omit<CustomerContextInterface, 'setIsWishlistLoading' | 'setWishlistItems'>;
};

export const CustomerContextProvider = ({ children, value }: CustomerContextProviderType) => {
  const [itemsInWishlist, setWishlistItems] = useState(value.itemsInWishlist);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);

  return (
    <CustomerContext.Provider
      value={{
        ...value,
        itemsInWishlist,
        isWishlistLoading,
        setWishlistItems,
        setIsWishlistLoading,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomerContext = () => {
  const context = useContext(CustomerContext);

  if (context === undefined || context === null) {
    throw new Error('useCustomerContext must be used within a CustomerContextProvider');
  }

  const {
    itemsInWishlist,
    isLoggedIn,
    isPerks,
    perksJoinDate,
    birthdayMonth,
    customerName,
    addressesCount,
    isWishlistLoading,
    customerPreferences,
    updateWishlist,
    setWishlistItems,
    setIsWishlistLoading,
    handleUpdateCustomerPreferences,
    createAccount,
    logIn,
  } = context;

  const handleUpdateWishlist: CustomerContextInterface['updateWishlist'] = async ({
    actionType,
    sku,
    variantId,
    eventData,
    notLoggedInAction = 'none',
  }) => {
    if (updateWishlist) {
      setIsWishlistLoading(true);
      if (eventData) {
        submitTracking({
          event: 'wishlist',
          action: actionType === 'addItem' ? 'add' : 'remove',
          trackingDescriptor: eventData.trackingDescriptor,
          gtm: eventData.gtm,
        });
        trackWishlistAlgoliaHit({
          ga4: eventData.ga4,
          trackingDescriptor: eventData.trackingDescriptor,
          action: actionType,
          sku,
        });
      }
      const { success, error } = await updateWishlist({ actionType, sku, variantId, eventData, notLoggedInAction });

      if (error) {
        console.error(`Error - could not ${actionType} item to/from wishlist`, error);
        setIsWishlistLoading(false);
        return { success: false, error: `${error}` };
      }

      if (success && setWishlistItems) {
        setWishlistItems(prevItems => {
          const wishlistItems = prevItems ?? [];
          return actionType === 'addItem' ? [...wishlistItems, sku] : wishlistItems.filter(item => item !== sku);
        });
        setIsWishlistLoading(false);
        return { success: true, error: undefined };
      }
    }
    return { success: false, error: 'updateWishlistFunction not provided' };
  };

  const getIsInWishlist = (sku: string): boolean => {
    return !!itemsInWishlist?.includes(sku);
  };

  return {
    isLoggedIn,
    isPerks,
    perksJoinDate,
    birthdayMonth,
    customerName,
    customerPreferences,
    addressesCount,
    isWishlistLoading,
    itemsInWishlist,
    getIsInWishlist,
    createAccount,
    logIn,
    handleUpdateWishlist,
    handleUpdateCustomerPreferences,
  };
};
